import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Activity, Brain, Waves, Users, Moon, Trophy } from "lucide-react";
import PageShell from "@/components/layout/PageShell";
import SubHero from "@/components/layout/SubHero";
import NextStep from "@/components/layout/NextStep";
import { Reveal, SectionOverline } from "@/components/landing/Shared";

const PILLARS = [
  { key: "exercise-physiology", icon: Activity, label: "01", title: "Exercise Physiology",
    lead: "How movement dose and modality change the underlying physiological state — the most direct input we have into the nervous system." },
  { key: "neuroplasticity", icon: Brain, label: "02", title: "Neuroplasticity",
    lead: "How repeated experience — chemical, electrical, and behavioral — reshapes the nervous system’s default responses." },
  { key: "stress-physiology", icon: Waves, label: "03", title: "Stress Physiology",
    lead: "The HPA axis, allostatic load, HRV, and vagal tone — the biology of load, recovery, and set-point." },
  { key: "behavioral-science", icon: Users, label: "04", title: "Behavioral Science",
    lead: "Environmental design, reinforcement, and habit formation — behavior as a physiological output rather than a moral choice." },
  { key: "recovery-science", icon: Moon, label: "05", title: "Recovery Science",
    lead: "Sleep architecture, parasympathetic access, and the mechanisms of adaptation and repair between doses of load." },
  { key: "human-performance", icon: Trophy, label: "06", title: "Human Performance",
    lead: "Autonomic capacity, ceiling expansion, and the physiology behind sustained, repeatable output under load." },
];

const FoundationsHub = () => {
  return (
    <PageShell>
      <SubHero
        testId="foundations-hero"
        breadcrumb={[{ label: "Home", to: "/" }, { label: "Scientific Foundations" }]}
        overline="Scientific Foundations"
        headline="The disciplines"
        headlineAccent="Adaptive Physiology™ stands on."
        lead="This section does not claim that Adaptive Physiology has been validated in a laboratory. It transparently maps the established scientific disciplines that inform and support the methodology — organized as knowledge pillars, not as a chronological archive."
        showAdaptiveLoop
        loopTestId="foundations-hub-loop"
      />

      {/* Posture statement */}
      <section
        data-testid="foundations-posture"
        className="relative py-16 sm:py-20 forr-canvas-alt border-b border-line-soft"
      >
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <Reveal>
            <div
              className="rounded-[24px] p-8 sm:p-12 max-w-4xl forr-surface"
              style={{ border: "1px solid var(--forr-line)", boxShadow: "var(--forr-shadow-md)" }}
            >
              <div className="forr-overline mb-4">Scientific Posture</div>
              <p className="font-display forr-text-ink text-2xl sm:text-3xl font-medium tracking-[-0.01em] leading-snug">
                Evidence-informed. Integrative, not inventive.
              </p>
              <p className="mt-6 text-base sm:text-lg font-light forr-text-soft leading-relaxed">
                Adaptive Physiology™ integrates established, peer-reviewed research
                from six disciplines. Where the science is strong, we apply it.
                Where it is still evolving, we say so. This section is where that
                integration is shown, transparently and in plain view.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Six pillars */}
      <section
        data-testid="foundations-pillars"
        className="relative py-24 sm:py-32 forr-canvas"
      >
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <Reveal>
            <SectionOverline
              number="→"
              label="Six Knowledge Pillars"
              testId="foundations-pillars-overline"
            />
          </Reveal>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {PILLARS.map((p, i) => (
              <Reveal delay={i * 0.06} key={p.key}>
                <Link
                  to={`/foundations/${p.key}`}
                  data-testid={`foundations-card-${p.key}`}
                  className="forr-card rounded-[24px] p-8 h-full block group"
                >
                  <div className="flex items-center justify-between mb-8">
                    <div
                      className="w-11 h-11 rounded-2xl flex items-center justify-center"
                      style={{ backgroundColor: "var(--forr-teal-tint)", color: "var(--forr-teal-deep)" }}
                    >
                      <p.icon size={20} strokeWidth={1.5} />
                    </div>
                    <span className="text-[11px] uppercase tracking-[0.24em] forr-text-faint">
                      Pillar {p.label}
                    </span>
                  </div>
                  <h3 className="font-display forr-text-ink text-xl font-medium tracking-tight mb-3">
                    {p.title}
                  </h3>
                  <p className="text-sm forr-text-soft font-light leading-relaxed">
                    {p.lead}
                  </p>
                  <div className="mt-8 inline-flex items-center gap-2 forr-text-teal-deep text-sm font-medium">
                    Open pillar
                    <ArrowRight
                      size={14}
                      strokeWidth={1.75}
                      className="transition-transform duration-300 group-hover:translate-x-1"
                    />
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <NextStep
        overline="Return"
        title="The Methodology"
        description="The framework these six disciplines converge into — Adaptive Physiology™."
        to="/methodology"
        cta="Open methodology"
        testId="foundations-hub-next"
      />
    </PageShell>
  );
};

export default FoundationsHub;
